import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
	ChevronDown,
	ChevronUp,
	FileText,
	Loader2,
} from "lucide-react";
import { useState } from "react";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Label } from "@/components/ui/label";
import { useTRPC } from "@/integrations/trpc/react";
import { useAuth } from "@/lib/auth";
import {
	getAuditActionColor,
	getAuditActionLabel,
} from "@/lib/utils";

export const Route = createFileRoute("/audit/")({
	component: AuditLogPage,
});

function AuditLogPage() {
	const { user } = useAuth();
	const trpc = useTRPC();
	const [expandedAuditId, setExpandedAuditId] = useState<string | null>(null);

	const { data: auditLogs, isLoading } = useQuery(
		trpc.audit.list.queryOptions({
			buyerOrgId: user?.organizationId ?? "",
			limit: 100,
			offset: 0,
		}),
	);

	if (isLoading) {
		return (
			<DashboardLayout>
				<div className="flex items-center justify-center h-64">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			</DashboardLayout>
		);
	}

	return (
		<DashboardLayout>
			<div className="space-y-6">
				<div>
					<h1 className="text-3xl font-bold tracking-tight">Audit Log</h1>
					<p className="text-muted-foreground">
						Complete history of all actions performed in the system
					</p>
				</div>

				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<FileText className="h-5 w-5" />
							Activity Audit Log
						</CardTitle>
						<CardDescription>
							This log is immutable and cannot be modified.
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						{/* Log Entries */}
						<div className="space-y-2">
							{(auditLogs ?? []).map((log) => (
								<Collapsible
									key={log.id}
									open={expandedAuditId === log.id}
									onOpenChange={(open) =>
										setExpandedAuditId(open ? log.id : null)
									}
								>
									<div className="border rounded-lg">
										<CollapsibleTrigger asChild>
											<div className="flex items-center justify-between p-4 hover:bg-muted/50 cursor-pointer">
												<div className="flex items-center gap-4">
													<Avatar className="h-8 w-8">
														<AvatarFallback className="text-xs">
															{(log.user_email ?? "?")
																.split("@")[0]
																.slice(0, 2)
																.toUpperCase()}
														</AvatarFallback>
													</Avatar>
													<div>
														<div className="flex items-center gap-2">
															<span className="font-medium">
																{log.user_email ?? "System"}
															</span>
															<Badge
																className={getAuditActionColor(log.action)}
															>
																{getAuditActionLabel(log.action)}
															</Badge>
															<span className="text-muted-foreground">
																{log.resource_type ?? "resource"}
															</span>
														</div>
													</div>
												</div>
												<div className="flex items-center gap-4">
													<span className="text-sm text-muted-foreground">
														{new Date(log.created_at).toLocaleString()}
													</span>
													{expandedAuditId === log.id ? (
														<ChevronUp className="h-4 w-4" />
													) : (
														<ChevronDown className="h-4 w-4" />
													)}
												</div>
											</div>
										</CollapsibleTrigger>
										<CollapsibleContent>
											<div className="px-4 pb-4 pt-0 border-t">
												<div className="grid gap-4 md:grid-cols-2 pt-4">
													<div>
														<Label className="text-muted-foreground">
															User ID
														</Label>
														<p className="text-sm font-mono">
															{log.user_id ?? "-"}
														</p>
													</div>
													{log.resource_id && (
														<div>
															<Label className="text-muted-foreground">
																Resource ID
															</Label>
															<p className="text-sm font-mono">
																{log.resource_id}
															</p>
														</div>
													)}
													{log.changed_fields &&
														Array.isArray(log.changed_fields) && (
															<div className="col-span-2">
																<Label className="text-muted-foreground">
																	Changed Fields
																</Label>
																<p className="text-sm font-mono">
																	{(log.changed_fields as string[]).join(", ")}
																</p>
															</div>
														)}
												</div>
												{log.old_data && log.new_data && (
													<div className="mt-4">
														<Label className="text-muted-foreground mb-2 block">
															Changes
														</Label>
														<div className="bg-muted/50 rounded-lg p-3 space-y-2 text-sm font-mono">
															<div>
																<span className="text-red-600">
																	- Old: {JSON.stringify(log.old_data)}
																</span>
															</div>
															<div>
																<span className="text-green-600">
																	+ New: {JSON.stringify(log.new_data)}
																</span>
															</div>
														</div>
													</div>
												)}
											</div>
										</CollapsibleContent>
									</div>
								</Collapsible>
							))}

							{(auditLogs ?? []).length === 0 && (
								<div className="text-center py-8 text-muted-foreground">
									<FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
									<p>No audit log entries found</p>
								</div>
							)}
						</div>

						<div className="text-center text-sm text-muted-foreground">
							Showing {(auditLogs ?? []).length} entries
						</div>
					</CardContent>
				</Card>
			</div>
		</DashboardLayout>
	);
}
